import { useState } from "react";
import { Archive, Trash2, RotateCcw, X, Clock, ChevronRight, DownloadCloud } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BoardControls({ completedCount, onClearCompleted, onResetBoard, onDataChanged }) {
  const [showArchives, setShowArchives] = useState(false);
  const [archives, setArchives] = useState([]);
  const [loadingArchives, setLoadingArchives] = useState(false);
  const [viewingArchive, setViewingArchive] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetLabel, setResetLabel] = useState("");
  const [restoring, setRestoring] = useState(false);

  const fetchArchives = async () => {
    setLoadingArchives(true);
    try {
      const res = await axios.get(`${API}/archives`);
      setArchives(res.data.archives || res.data);
    } catch (e) {
      toast.error("Failed to load archives");
    } finally {
      setLoadingArchives(false);
    }
  };

  const viewArchive = async (archiveId) => {
    try {
      const res = await axios.get(`${API}/archives/${archiveId}`);
      setViewingArchive(res.data);
    } catch (e) {
      toast.error("Failed to load archive");
    }
  };

  const deleteArchive = async (archiveId) => {
    try {
      await axios.delete(`${API}/archives/${archiveId}`);
      setArchives((prev) => prev.filter((a) => a.id !== archiveId));
      if (viewingArchive?.id === archiveId) setViewingArchive(null);
      toast.success("Archive deleted");
    } catch (e) {
      toast.error("Failed to delete archive");
    }
  };

  const handleOpenArchives = () => {
    fetchArchives();
    setShowArchives(true);
  };

  const handleRestore = async (archiveId) => {
    setRestoring(true);
    try {
      const res = await axios.post(`${API}/archives/${archiveId}/restore`);
      toast.success(`Restored ${res.data.restored} tasks`);
      if (onDataChanged) onDataChanged();
      setViewingArchive(null);
      setShowArchives(false);
    } catch {
      toast.error("Failed to restore archive");
    } finally {
      setRestoring(false);
    }
  };

  const handleReset = () => {
    onResetBoard(resetLabel);
    setShowResetConfirm(false);
    setResetLabel("");
  };

  return (
    <>
      <div className="board-controls">
        {completedCount > 0 && (
          <button
            className="board-ctrl-btn"
            onClick={onClearCompleted}
            title="Clear completed tasks"
            data-testid="clear-completed-btn"
          >
            <Trash2 size={13} />
            <span>Clear done ({completedCount})</span>
          </button>
        )}
        <button
          className="board-ctrl-btn board-ctrl-reset"
          onClick={() => setShowResetConfirm(true)}
          title="Archive board and start fresh"
          data-testid="reset-board-btn"
        >
          <RotateCcw size={13} />
          <span>New day</span>
        </button>
        <button
          className="board-ctrl-btn"
          onClick={handleOpenArchives}
          title="View archived sessions"
          data-testid="view-archives-btn"
        >
          <Archive size={13} />
          <span>History</span>
        </button>
      </div>

      {/* Reset Confirm Dialog */}
      {showResetConfirm && (
        <div className="dialog-overlay" onClick={() => setShowResetConfirm(false)}>
          <div className="dialog-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div className="dialog-title">Start a New Day</div>
            <p style={{ color: "var(--fg-secondary)", fontSize: "0.8125rem", marginBottom: 16 }}>
              This will archive your current board and clear all tasks. Templates will be kept.
            </p>
            <div className="dialog-field">
              <label className="dialog-label">Session Label (optional)</label>
              <input
                className="dialog-input"
                placeholder="e.g., Monday AM, Sprint 3..."
                value={resetLabel}
                onChange={(e) => setResetLabel(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleReset()}
                autoFocus
              />
            </div>
            <div className="dialog-actions">
              <button className="dialog-btn" onClick={() => setShowResetConfirm(false)}>Cancel</button>
              <button className="dialog-btn dialog-btn-primary" onClick={handleReset}>Archive & Reset</button>
            </div>
          </div>
        </div>
      )}

      {/* Archives Modal */}
      {showArchives && (
        <div className="dialog-overlay" onClick={() => { setShowArchives(false); setViewingArchive(null); }}>
          <div className="dialog-content archive-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div className="dialog-title" style={{ margin: 0 }}>Session History</div>
              <button className="bubble-action-btn" onClick={() => { setShowArchives(false); setViewingArchive(null); }}>
                <X size={16} />
              </button>
            </div>

            {!viewingArchive ? (
              <div className="archive-list">
                {loadingArchives ? (
                  <div style={{ textAlign: "center", padding: 24, color: "var(--fg-subtle)" }}>Loading...</div>
                ) : archives.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 24, color: "var(--fg-subtle)" }}>
                    No archived sessions yet. Click "New day" to archive your first board.
                  </div>
                ) : (
                  archives.map((a) => (
                    <div key={a.id} className="archive-item">
                      <div className="archive-item-info" onClick={() => viewArchive(a.id)}>
                        <Clock size={13} />
                        <div>
                          <div className="archive-item-label">{a.label}</div>
                          <div className="archive-item-meta">
                            {a.summary?.total || 0} tasks, {a.summary?.completed || 0} completed
                          </div>
                        </div>
                        <ChevronRight size={14} style={{ marginLeft: "auto", opacity: 0.4 }} />
                      </div>
                      <button className="archive-delete-btn" onClick={() => deleteArchive(a.id)} title="Delete archive">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <div className="archive-detail">
                <button className="archive-back-btn" onClick={() => setViewingArchive(null)}>
                  <ChevronRight size={14} style={{ transform: "rotate(180deg)" }} /> Back
                </button>
                <div className="archive-detail-label">{viewingArchive.label}</div>
                <div className="archive-detail-meta" style={{ marginBottom: 8 }}>
                  {viewingArchive.created_at?.split("T")[0]} &middot; {viewingArchive.snapshot?.length || 0} tasks
                </div>
                <button
                  className="dialog-btn dialog-btn-primary archive-restore-btn"
                  onClick={() => handleRestore(viewingArchive.id)}
                  disabled={restoring}
                  data-testid="restore-archive-btn"
                >
                  <DownloadCloud size={13} />
                  <span>{restoring ? "Restoring..." : "Restore all tasks"}</span>
                </button>
                <div className="archive-snapshot-list">
                  {(viewingArchive.snapshot || []).map((t) => (
                    <div key={t.id} className="archive-task-row">
                      <span className={`archive-task-check ${t.completed ? "done" : ""}`}>
                        {t.completed && "\u2713"}
                      </span>
                      <span className={`archive-task-name ${t.completed ? "done" : ""}`}>{t.name}</span>
                      <span className={`meta-badge meta-badge-${t.category}`}>{t.category}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
